/**
 * settingsCatalogue — the twenty five settings entries and where each belongs.
 *
 * SETTINGS-SPLIT-1. One row per entry in `navConfig`'s settings section, with
 * the bucket that decides whether it shows. Data, not logic: the decision is in
 * `settingsVisibility.ts` and this is what it decides over.
 *
 * The ids match `navConfig` so the two cannot drift apart silently — a guard
 * asserts that every settings child in navConfig appears here exactly once.
 */

import type { SettingsEntry } from './settingsVisibility'

export const SETTINGS_CATALOGUE: readonly SettingsEntry[] = [
  // PLATFORM — everybody needs it, whatever they do.
  { id: 'settings_account', label: 'Account', bucket: 'platform' },
  { id: 'settings_branding', label: 'Branding', bucket: 'platform' },
  { id: 'settings_themes', label: 'Themes', bucket: 'platform' },
  { id: 'settings_menu', label: 'Menu', bucket: 'platform' },
  { id: 'settings_notifications', label: 'Notifications', bucket: 'platform' },
  { id: 'settings_your_words', label: 'Your words', bucket: 'platform' },
  { id: 'settings_ghost', label: 'Ghost', bucket: 'platform' },
  // THE-ACCOUNT-REMEMBERS-2 — every account has a memory, whatever it does.
  { id: 'settings_memory', label: 'What Ghost remembers', bucket: 'platform' },
  // THE-DIVIDER-HOLDS-ONLY-WHAT-IS-RULED-1 — the wallet and the bill. Money
  // stopped being fixed furniture, so the two pages that are nobody's build live
  // here: every account has a plan, a credit balance and a fee tier from the
  // moment it exists, whatever it has switched on.
  { id: 'money_plan_and_usage', label: 'Plan and usage', bucket: 'platform' },
  { id: 'money_fees', label: 'Fees', bucket: 'platform' },

  // THE SHELF — another lane owns what is on it. These are the way in.
  { id: 'settings_modules', label: 'What this can do', bucket: 'shelf' },
  { id: 'settings_record_types', label: 'Your own record types', bucket: 'shelf' },
  { id: 'settings_custom_fields', label: 'Your own fields', bucket: 'shelf' },

  // BLOCK — belongs to a section, and appears when that section is on.
  { id: 'settings_business_hours', label: 'Business hours', bucket: 'block', module: 'schedule' },
  { id: 'settings_calendar_sync', label: 'Calendar sync', bucket: 'block', module: 'schedule' },
  { id: 'settings_messages', label: 'Messages', bucket: 'block', module: 'messages' },
  { id: 'settings_public_quote', label: 'Public quote', bucket: 'block', module: 'sales' },
  { id: 'settings_lead_sources', label: 'Lead sources', bucket: 'block', module: 'sales' },
  {
    id: 'settings_customer_portal',
    label: 'Customer portal',
    bucket: 'block',
    module: 'customers',
  },
  { id: 'settings_artifacts', label: 'Artifacts', bucket: 'block', module: 'settings' },

  // TRADE — belongs in a template, not in the product. Shown only to an account
  // that is actually doing the trade, judged by what it has done.
  { id: 'settings_service_area', label: 'Service area', bucket: 'trade', trade: 'territory' },
  {
    id: 'settings_canvass_dispositions',
    label: 'Canvass dispositions',
    bucket: 'trade',
    trade: 'canvassing',
  },
  { id: 'settings_pay_periods', label: 'Pay periods', bucket: 'trade', trade: 'payroll' },
  { id: 'settings_bonuses', label: 'Bonuses', bucket: 'trade', trade: 'payroll' },

  // BACKEND — a merchant never sees it.
  { id: 'settings_affiliate_payouts', label: 'Affiliate payouts', bucket: 'backend' },
  { id: 'settings_affiliate_program', label: 'Affiliate program', bucket: 'backend' },
  { id: 'settings_import_from_another_crm', label: 'Import from another CRM', bucket: 'backend' },
]

/** How many of each bucket, so the report and the guard read the same numbers. */
export function bucketCounts(): Record<string, number> {
  const out: Record<string, number> = {}
  for (const e of SETTINGS_CATALOGUE) out[e.bucket] = (out[e.bucket] ?? 0) + 1
  return out
}
